/**
 * supabaseClarifications.ts — CRUD-Schicht für `case_clarifications`
 * (async Rückfragen-Channel zwischen Berater und Mandant).
 *
 * Workflow siehe Migration 0017:
 *   1. createClarification(caseId, fieldPath, question)  → Berater fragt nach
 *   2. listClarificationsForCase(caseId)                 → beide Seiten sehen
 *   3. answerClarification(id, answer)                   → Mandant antwortet
 *   4. resolveClarification(id)                          → Berater bestätigt
 *
 * Sicherheits-Garantien (RLS + Trigger 0017):
 *   - SELECT/INSERT/UPDATE über can_access_case() — symmetrisch für beide Seiten.
 *   - created_by_role wird DB-seitig aus public.users.role gesetzt.
 *   - Append-only nach Insert: question/created_* immutable.
 *   - Antwort: nur einmalig, nicht durch Fragesteller selbst, answered_by = auth.uid().
 *   - Resolve: nur Berater/Admin, einmalig, resolved_by = auth.uid().
 *
 * Was diese Schicht bewusst NICHT tut:
 *   - Keine Push-Notifications/Emails — separater Worker (Phase A.2 oder A.3).
 *     Mandant sieht offene Rückfragen beim nächsten Login; Berater sieht
 *     beantwortete Rückfragen im Cockpit-Badge.
 *   - Keine Aggregation pro Wizard-Step — UI gruppiert auf Frontend-Seite,
 *     damit ein einziger DB-Roundtrip pro Case-Page reicht.
 */

import { getSupabaseClient } from './supabase';
import type { CaseClarificationRow, Database } from './supabaseTypes';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const QUESTION_MAX_LEN = 2000;
const ANSWER_MAX_LEN = 4000;
const FIELD_PATH_MAX_LEN = 200;

export type CaseClarification = CaseClarificationRow;

export interface CreateClarificationInput {
  readonly caseId: string;
  readonly fieldPath: string;
  /** Optionaler menschen-lesbarer Label für das Feld (z.B. "Umsatz Q1 2026"). */
  readonly fieldLabel?: string;
  readonly question: string;
}

/**
 * Legt eine neue Rückfrage an. RLS+Trigger garantieren, dass der eingeloggte
 * User Case-Zugriff hat und die created_by_role automatisch aus
 * public.users.role abgeleitet wird.
 *
 * Validation hier nur die UX-relevanten Längen-/Format-Checks; die DB hat
 * dieselben CHECK-Constraints und würde ansonsten einen unleserlichen
 * Fehler werfen.
 */
export async function createClarification(
  input: CreateClarificationInput,
): Promise<CaseClarification> {
  if (!UUID_RE.test(input.caseId)) {
    throw new Error('createClarification: caseId ist kein UUID-Format');
  }
  const fieldPath = input.fieldPath.trim();
  if (fieldPath.length === 0 || fieldPath.length > FIELD_PATH_MAX_LEN) {
    throw new Error(
      `createClarification: fieldPath muss 1..${FIELD_PATH_MAX_LEN.toString()} Zeichen sein`,
    );
  }
  const question = input.question.trim();
  if (question.length === 0 || question.length > QUESTION_MAX_LEN) {
    throw new Error(
      `createClarification: question muss 1..${QUESTION_MAX_LEN.toString()} Zeichen sein`,
    );
  }

  const sb = getSupabaseClient();
  const { data: userData, error: userErr } = await sb.auth.getUser();
  if (userErr) {
    throw new Error(`createClarification: auth.getUser failed — ${userErr.message}`);
  }
  const uid = userData.user?.id;
  if (typeof uid !== 'string') {
    throw new Error('createClarification: kein authentifizierter User');
  }

  const insertPayload: Database['public']['Tables']['case_clarifications']['Insert'] = {
    case_id: input.caseId,
    field_path: fieldPath,
    created_by: uid,
    question,
    ...(input.fieldLabel !== undefined ? { field_label: input.fieldLabel.trim() || null } : {}),
  };

  const { data, error } = await sb
    .from('case_clarifications')
    .insert(insertPayload as never)
    .select('*')
    .single();

  if (error) {
    throw new Error(`createClarification [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

export interface ListClarificationsOptions {
  /** Wenn true: nur resolved_at IS NULL. Default: false (alles laden). */
  readonly onlyOpen?: boolean;
}

/**
 * Listet alle Rückfragen eines Case (RLS sichert Berechtigung).
 * Sortierung: neueste zuerst — bietet sich für eine "Letzte Aktivität"-Liste
 * im Cockpit an.
 */
export async function listClarificationsForCase(
  caseId: string,
  opts: ListClarificationsOptions = {},
): Promise<CaseClarification[]> {
  if (!UUID_RE.test(caseId)) return [];

  const sb = getSupabaseClient();
  let q = sb.from('case_clarifications').select('*').eq('case_id', caseId);
  if (opts.onlyOpen === true) {
    q = q.is('resolved_at', null);
  }
  const { data, error } = await q.order('created_at', { ascending: false });

  if (error) {
    throw new Error(`listClarificationsForCase [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

/**
 * Beantwortet eine offene Rückfrage. RLS+Trigger 0017 enforcen:
 *   - User darf NICHT der Fragesteller sein
 *   - answered_by = auth.uid() (server-seitig erzwungen)
 *   - answered_at darf vorher NULL sein
 *
 * Bei Verletzung wirft die DB einen 'insufficient_privilege'-Fehler.
 */
export async function answerClarification(
  clarificationId: string,
  answer: string,
): Promise<CaseClarification> {
  if (!UUID_RE.test(clarificationId)) {
    throw new Error('answerClarification: clarificationId ist kein UUID-Format');
  }
  const trimmedAnswer = answer.trim();
  if (trimmedAnswer.length === 0 || trimmedAnswer.length > ANSWER_MAX_LEN) {
    throw new Error(
      `answerClarification: answer muss 1..${ANSWER_MAX_LEN.toString()} Zeichen sein`,
    );
  }

  const sb = getSupabaseClient();
  const { data: userData, error: userErr } = await sb.auth.getUser();
  if (userErr) {
    throw new Error(`answerClarification: auth.getUser failed — ${userErr.message}`);
  }
  const uid = userData.user?.id;
  if (typeof uid !== 'string') {
    throw new Error('answerClarification: kein authentifizierter User');
  }

  const update: Database['public']['Tables']['case_clarifications']['Update'] = {
    answer: trimmedAnswer,
    answered_at: new Date().toISOString(),
    answered_by: uid,
  };

  const { data, error } = await sb
    .from('case_clarifications')
    .update(update as never)
    .eq('id', clarificationId)
    // Defensiv: nur antworten, wenn noch nicht beantwortet.
    .is('answered_at', null)
    .select('*')
    .single();

  if (error) {
    throw new Error(`answerClarification [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

/**
 * Markiert eine Rückfrage als gelöst. RLS+Trigger 0017 erlauben das nur
 * für Berater/Admin. Eine Rückfrage darf auch ohne Antwort gelöst werden
 * (z.B. wenn der Berater die Information aus anderer Quelle bekommen hat).
 */
export async function resolveClarification(clarificationId: string): Promise<CaseClarification> {
  if (!UUID_RE.test(clarificationId)) {
    throw new Error('resolveClarification: clarificationId ist kein UUID-Format');
  }

  const sb = getSupabaseClient();
  const { data: userData, error: userErr } = await sb.auth.getUser();
  if (userErr) {
    throw new Error(`resolveClarification: auth.getUser failed — ${userErr.message}`);
  }
  const uid = userData.user?.id;
  if (typeof uid !== 'string') {
    throw new Error('resolveClarification: kein authentifizierter User');
  }

  const update: Database['public']['Tables']['case_clarifications']['Update'] = {
    resolved_at: new Date().toISOString(),
    resolved_by: uid,
  };

  const { data, error } = await sb
    .from('case_clarifications')
    .update(update as never)
    .eq('id', clarificationId)
    .is('resolved_at', null)
    .select('*')
    .single();

  if (error) {
    throw new Error(`resolveClarification [${error.code ?? '??'}]: ${error.message}`);
  }
  return data;
}

/**
 * Aggregierte Counts pro Status. Für Cockpit-Badges + Mandant-Notifikation.
 *
 * - total: alle Rückfragen des Case (unabhängig von Status)
 * - unanswered: created, noch keine Antwort
 * - awaitingResolution: beantwortet, noch nicht resolved
 * - resolved: resolved
 *
 * Implementierungs-Notiz: ein einziger Roundtrip mit count='exact' wäre
 * theoretisch günstiger, aber RLS-Filter + count-Modi gemischt mit Filter-
 * Kombinationen verhalten sich in Supabase v2 inkonsistent. Wir laden
 * stattdessen die Liste und aggregieren clientseitig — bei <1000 Rückfragen
 * pro Case (realistisch <30) ist das billig genug.
 */
export interface ClarificationCounts {
  readonly total: number;
  readonly unanswered: number;
  readonly awaitingResolution: number;
  readonly resolved: number;
}

export async function getClarificationCountsForCase(caseId: string): Promise<ClarificationCounts> {
  if (!UUID_RE.test(caseId)) {
    return { total: 0, unanswered: 0, awaitingResolution: 0, resolved: 0 };
  }

  const sb = getSupabaseClient();
  const { data, error } = await sb
    .from('case_clarifications')
    .select('answered_at, resolved_at')
    .eq('case_id', caseId);

  if (error) {
    throw new Error(`getClarificationCountsForCase [${error.code ?? '??'}]: ${error.message}`);
  }

  const rows = (data ?? []) as Array<Pick<CaseClarification, 'answered_at' | 'resolved_at'>>;
  let unanswered = 0;
  let awaitingResolution = 0;
  let resolved = 0;
  for (const r of rows) {
    if (r.resolved_at !== null) {
      resolved += 1;
    } else if (r.answered_at !== null) {
      awaitingResolution += 1;
    } else {
      unanswered += 1;
    }
  }
  return {
    total: rows.length,
    unanswered,
    awaitingResolution,
    resolved,
  };
}

/**
 * Phase B: Bulk-Counts für eine Liste von Case-IDs.
 *
 * Strategie:
 *   1. Wenn die View `case_clarification_counts` existiert (Backend-Migration
 *      0020), nutzen wir die — eine SELECT, exakt was wir brauchen.
 *   2. Falls die View noch nicht da ist (404 / „relation does not exist"):
 *      Fallback auf `case_clarifications` mit `IN (caseIds)` und gruppieren
 *      clientseitig. Auch ein Roundtrip, aber ohne die View.
 *   3. Wenn auch das fehlschlägt: leere Map zurückgeben — der Aufrufer ist
 *      angewiesen, das defensiv zu behandeln (Cases-Liste rendert auch
 *      ohne Badges).
 *
 * Rückgabe: Map<caseId, ClarificationCounts>. CaseIds, die keinen Eintrag
 * in case_clarifications haben, fehlen in der Map (= alle Counts 0 — der
 * Aufrufer kann mit `?? { total: 0, unanswered: 0, … }` defaulten).
 */
export async function getClarificationCountsForCases(
  caseIds: ReadonlyArray<string>,
): Promise<ReadonlyMap<string, ClarificationCounts>> {
  // Defensive: leere Eingabe = leere Ausgabe, kein Roundtrip.
  if (caseIds.length === 0) return new Map();

  const validIds = caseIds.filter((id) => UUID_RE.test(id));
  if (validIds.length === 0) return new Map();

  const sb = getSupabaseClient();

  // Pfad 1: View case_clarification_counts (wenn vom Backend-Agenten geliefert)
  try {
    const { data, error } = await (
      sb as unknown as {
        from: (table: string) => {
          select: (cols: string) => {
            in: (
              col: string,
              vals: ReadonlyArray<string>,
            ) => Promise<{
              data: Array<{
                case_id: string;
                total: number;
                unanswered: number;
                awaiting_resolution: number;
                resolved: number;
              }> | null;
              error: { code?: string; message: string } | null;
            }>;
          };
        };
      }
    )
      .from('case_clarification_counts')
      .select('case_id, total, unanswered, awaiting_resolution, resolved')
      .in('case_id', validIds);

    if (!error && data !== null) {
      const map = new Map<string, ClarificationCounts>();
      for (const row of data) {
        map.set(row.case_id, {
          total: Number(row.total) || 0,
          unanswered: Number(row.unanswered) || 0,
          awaitingResolution: Number(row.awaiting_resolution) || 0,
          resolved: Number(row.resolved) || 0,
        });
      }
      return map;
    }
    // View existiert noch nicht ODER RLS blockt → Fallback unten
  } catch {
    // Pragma: silent fall-through zum Fallback
  }

  // Pfad 2: Fallback auf case_clarifications direkt
  try {
    const { data, error } = await sb
      .from('case_clarifications')
      .select('case_id, answered_at, resolved_at')
      .in('case_id', validIds);

    if (error) {
      console.warn(
        `getClarificationCountsForCases [fallback ${error.code ?? '??'}]: ${error.message}`,
      );
      return new Map();
    }

    const rows = (data ?? []) as Array<{
      case_id: string;
      answered_at: string | null;
      resolved_at: string | null;
    }>;
    const map = new Map<string, ClarificationCounts>();
    for (const r of rows) {
      const prev = map.get(r.case_id) ?? {
        total: 0,
        unanswered: 0,
        awaitingResolution: 0,
        resolved: 0,
      };
      const next = {
        total: prev.total + 1,
        unanswered: prev.unanswered + (r.resolved_at === null && r.answered_at === null ? 1 : 0),
        awaitingResolution:
          prev.awaitingResolution + (r.resolved_at === null && r.answered_at !== null ? 1 : 0),
        resolved: prev.resolved + (r.resolved_at !== null ? 1 : 0),
      };
      map.set(r.case_id, next);
    }
    return map;
  } catch (err) {
    console.warn('getClarificationCountsForCases [fallback exception]', err);
    return new Map();
  }
}
